'use client';

/**
 * PayPal checkout-url 整页跳转（订阅；一次性在 v6 不可用时 fallback）：
 * 1. commerce/order/create/v1（return_scene + scene 参数）→ store_payload.checkout_url
 * 2. 埋点上下文 + return_url/cancel_url 写入 sessionStorage（Safari 剥参回跳恢复用）
 * 3. window.location.replace(checkout_url)
 * 4. 用户在 PayPal 完成支付 → 后端按 return_scene 拼回跳 URL
 * 5. HostedCheckoutReturnConfirmHandler 完成 confirm 与结果埋点
 *
 * 不接 PayPal JS SDK。
 *
 * 性能监控：调用方传入 perfTiming 后，跳转前会上报「点击 -> 跳转」链路各阶段耗时
 * （详见 reportPaypalRedirectDuration）。
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { getPayPalPayMetaSessionKey, getPayPalSubsMetaSessionKey } from '@/constants/storage';
import { persistPayReturnUrlsFromCreateOrder } from '@/lib/pay/hosted-checkout-return-restore';
import { readArmsFailDetail } from '@/utils/arms/common';
import { reportPaypalRedirectDuration } from '@/utils/arms/performance-events';

import type { ICreateOrderData } from '@/apis/commerce';
import type { IPayPalCheckoutArmsFailContext, TPayPalV6CheckoutChannel } from '@/utils/arms/paypal-events';
import type { IPayPendingResultMeta } from '@/utils/tracker/pay';

export { persistPayReturnUrlsFromCreateOrder } from '@/lib/pay/hosted-checkout-return-restore';

export function persistPayPendingMeta(orderId: string, meta: IPayPendingResultMeta): void {
    if (typeof window === 'undefined' || !orderId) {
        return;
    }
    try {
        window.sessionStorage.setItem(getPayPalPayMetaSessionKey(orderId), JSON.stringify(meta));
    } catch {
        // ignore
    }
}

function parsePayPendingMeta(parsed: Partial<IPayPendingResultMeta>): IPayPendingResultMeta | null {
    if (typeof parsed.store !== 'string') {
        return null;
    }
    return {
        store: parsed.store,
        trigger_scene: typeof parsed.trigger_scene === 'string' ? parsed.trigger_scene : '',
        gear_id: typeof parsed.gear_id === 'string' ? parsed.gear_id : '',
        is_balance_enough:
            typeof parsed.is_balance_enough === 'number'
                ? parsed.is_balance_enough
                : Number(parsed.is_balance_enough) || 0,
        paywall_template_id: typeof parsed.paywall_template_id === 'string' ? parsed.paywall_template_id : '',
        version: typeof parsed.version === 'number' ? parsed.version : Number(parsed.version) || 0,
        checksum: typeof parsed.checksum === 'string' ? parsed.checksum : '',
        book_id: typeof parsed.book_id === 'string' ? parsed.book_id : '',
        chapter_id: typeof parsed.chapter_id === 'string' ? parsed.chapter_id : '',
        chapter_price:
            parsed.chapter_price !== undefined && parsed.chapter_price !== null
                ? (parsed.chapter_price as number | string)
                : '',
        is_auto_unlock_on:
            parsed.is_auto_unlock_on !== undefined && parsed.is_auto_unlock_on !== null
                ? (parsed.is_auto_unlock_on as number | string)
                : '',
        action_type: 'pay',
        goods_type: parsed.goods_type === 'recharge' ? 'recharge' : 'subscribe',
        money: typeof parsed.money === 'number' ? parsed.money : Number(parsed.money) || 0,
        is_renew: parsed.is_renew === 2 ? 2 : parsed.is_renew === 1 ? 1 : undefined,
        coin: typeof parsed.coin === 'number' ? parsed.coin : undefined,
        bonus: typeof parsed.bonus === 'number' ? parsed.bonus : undefined,
        payment_method: typeof parsed.payment_method === 'string' ? parsed.payment_method : '',
    };
}

function readRawMeta(orderId: string): string | null {
    return (
        window.sessionStorage.getItem(getPayPalPayMetaSessionKey(orderId)) ??
        window.sessionStorage.getItem(getPayPalSubsMetaSessionKey(orderId))
    );
}

export function readPersistedPayPendingMeta(orderId: string): IPayPendingResultMeta | null {
    if (typeof window === 'undefined' || !orderId) {
        return null;
    }
    try {
        const raw = readRawMeta(orderId);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as Partial<
            IPayPendingResultMeta & { productId?: string; triggerScene?: string }
        >;
        // 兼容旧版仅含 store / productId / triggerScene 的缓存
        if (typeof parsed.store === 'string' && parsed.trigger_scene === undefined) {
            return {
                store: parsed.store,
                trigger_scene: typeof parsed.triggerScene === 'string' ? parsed.triggerScene : '',
                gear_id: typeof parsed.productId === 'string' ? parsed.productId : '',
                is_balance_enough: 0,
                paywall_template_id: '',
                version: 0,
                checksum: '',
                book_id: '',
                chapter_id: '',
                chapter_price: '',
                is_auto_unlock_on: '',
                action_type: 'pay',
                goods_type: 'subscribe',
                money: 0,
                is_renew: 1,
                payment_method: '',
            };
        }
        if (typeof parsed.store !== 'string') {
            return null;
        }
        return parsePayPendingMeta(parsed);
    } catch {
        return null;
    }
}

export function clearPersistedPayPendingMeta(orderId: string): void {
    if (typeof window === 'undefined' || !orderId) {
        return;
    }
    try {
        window.sessionStorage.removeItem(getPayPalPayMetaSessionKey(orderId));
        window.sessionStorage.removeItem(getPayPalSubsMetaSessionKey(orderId));
    } catch {
        // ignore
    }
}

export interface IStartPayPalCheckoutRedirectParams {
    /** 调 commerce/order/create/v1，返回 store_payload.checkout_url */
    createCommerceOrder: () => Promise<ICreateOrderData>;
    /** 整页跳转 PayPal 前调用（H5 无法在 paypal.com 内感知真实曝光） */
    onGatewayShow?: (orderData: ICreateOrderData) => void;
    onError?: (message: string, armsFail?: IPayPalCheckoutArmsFailContext) => void;
    /**
     * 性能监控：传入后在跳转前统一上报「点击档位 -> 触发 PayPal 跳转」各阶段耗时。
     * 不传则跳过上报（如旧调用方未接入）。
     */
    perfTiming?: {
        /** 点击档位时由调用方记录的时间戳（Date.now()） */
        clickTime: number;
        /** 书籍 ID */
        book_id: string;
        /** 章节 ID（触发付费的锚点章节） */
        chapter_id: string;
        /** 用户选择的档位 product_id */
        gear_id: string;
        /** 商品类型：'subscribe'（订阅）| 'recharge'（充值） */
        goods_type: string;
    };
    /** 出站渠道，默认 checkout_url */
    checkout_channel?: TPayPalV6CheckoutChannel;
}

/** PayPal redirect 兜底超时：>10s 仍未卸载页面则强制复位 redirecting，防 mask 永久卡死。 */
const REDIRECT_GUARD_TIMEOUT_MS = 10_000;

export function usePayPalCheckoutRedirect() {
    const [paypalPaying, setPaypalPaying] = useState(false);
    const guardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearGuardTimer = useCallback(() => {
        if (guardTimerRef.current !== null) {
            clearTimeout(guardTimerRef.current);
            guardTimerRef.current = null;
        }
    }, []);

    useEffect(() => () => clearGuardTimer(), []);

    // 创建订单 -> 触发 PayPal 跳转
    const startCheckoutRedirect = useCallback(async (params: IStartPayPalCheckoutRedirectParams) => {
        setPaypalPaying(true);

        clearGuardTimer();
        guardTimerRef.current = setTimeout(() => {
            guardTimerRef.current = null;
            setPaypalPaying(false);
            params.onError?.('Payment gateway timeout, please try again');
        }, REDIRECT_GUARD_TIMEOUT_MS);

        // 记录发出创单请求前的时间（与 clickTime 的差值 = 同步准备耗时）
        const apiStartTime = Date.now();

        let data: ICreateOrderData;
        try {
            data = await params.createCommerceOrder();
        } catch (err) {
            clearGuardTimer();
            setPaypalPaying(false);
            params.onError?.('Failed to create order', {
                fail_reason: 'order_create_api_failed',
                fail_detail: readArmsFailDetail(err),
            });
            return;
        }

        // 记录创单请求返回后的时间（与 apiStartTime 的差值 = 创单接口网络耗时）
        const apiEndTime = Date.now();

        const checkoutUrl = data.store_payload?.checkout_url;
        if (!checkoutUrl) {
            clearGuardTimer();
            setPaypalPaying(false);
            params.onError?.('Missing PayPal checkout url', {
                fail_reason: 'order_create_missing_checkout_url',
                order_id: data.order_id,
            });
            return;
        }

        persistPayReturnUrlsFromCreateOrder(data);

        // replace 之后页面会卸载；若 10s 内仍未卸载（被弹窗拦截 / 静默失败），兜底定时器会复位 redirecting
        params.onGatewayShow?.(data);

        // 上报「点击档位 -> 触发 PayPal 跳转」各阶段耗时
        const preRedirectTime = Date.now();
        if (params.perfTiming) {
            const { clickTime, ...ctx } = params.perfTiming;
            const redirectPayload = {
                ...ctx,
                page_path: window.location.pathname,
                order_id: data.order_id ?? '',
                total_time: preRedirectTime - clickTime, // 完整链路耗时
                before_fetch_time: apiStartTime - clickTime, // 请求前前端逻辑执行耗时
                fetch_time: apiEndTime - apiStartTime, // 接口网络耗时
                after_fetch_time: preRedirectTime - apiEndTime, // 接口返回后前端逻辑执行耗时
                checkout_channel: params.checkout_channel ?? 'checkout_url',
            };
            reportPaypalRedirectDuration(redirectPayload);
        }

        setPaypalPaying(false);
        window.location.href = checkoutUrl;
    }, []);

    return { paypalPaying, startCheckoutRedirect };
}
